/**
 * AdminAddUseCasesPage - [UC-056]
 * Admin page for managing use cases on a project - add, edit, delete, set prices and mandatory flags.
 * Design matches admin-manage-use-cases.screen.md
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../core/api-client';
import toast from 'react-hot-toast';
import { fmt, fmtMoney } from '../utils/format';

interface UseCase {
  projectUseCaseId?: number;
  name: string;
  // null while the admin hasn't typed a value yet — lets us render an
  // empty cell instead of a misleading `0` default for fresh rows.
  price: number | null;
  mandatory: boolean;
  deleted?: boolean;
  isNew?: boolean;
}

interface ProjectDetail {
  projectId: number;
  name: string;
  useCases: UseCase[];
}

interface FieldError {
  name?: string;
  price?: string;
}

export function AdminAddUseCasesPage() {
  const { id: projectId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [useCases, setUseCases] = useState<UseCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<number, FieldError>>({});

  useEffect(() => {
    const loadProject = async () => {
      if (!projectId) return;

      try {
        setLoading(true);
        const data = await api.get<ProjectDetail>(`/projects/${projectId}`);
        setProject(data);
        const loaded = (data.useCases || []).map(uc => ({
          ...uc,
          price: Number(uc.price) || 0,
        }));
        setUseCases(loaded);
      } catch (error) {
        console.error('Failed to load project:', error);
        setShowErrorMessage(true);
        setErrorMessage(t('adminAddUseCases.loadError', 'Failed to load project'));
      } finally {
        setLoading(false);
      }
    };

    loadProject();
  }, [projectId, t]);

  const addNewUseCase = () => {
    setUseCases([...useCases, { name: '', price: null, mandatory: false, isNew: true }]);
  };

  const updateUseCase = (index: number, field: keyof UseCase, value: any) => {
    const updated = [...useCases];
    updated[index] = { ...updated[index], [field]: value };
    setUseCases(updated);
    // Clear field error
    if (fieldErrors[index]) {
      const newErrors = { ...fieldErrors };
      delete newErrors[index];
      setFieldErrors(newErrors);
    }
  };

  const removeUseCase = (index: number) => {
    const useCase = useCases[index];
    if (useCase.isNew) {
      setUseCases(useCases.filter((_, i) => i !== index));
    } else {
      updateUseCase(index, 'deleted', true);
    }
  };

  const toggleMandatory = (index: number) => {
    updateUseCase(index, 'mandatory', !useCases[index].mandatory);
  };

  const validateUseCases = (): boolean => {
    const errors: Record<number, FieldError> = {};
    let isValid = true;

    useCases.forEach((uc, index) => {
      if (uc.deleted) return;
      const err: FieldError = {};
      if (!uc.name?.trim()) {
        err.name = t('adminAddUseCases.nameRequired', 'Use case name is required');
        isValid = false;
      }
      if (!uc.price || uc.price <= 0) {
        err.price = t('adminAddUseCases.pricePositive', 'Price must be greater than zero');
        isValid = false;
      }
      if (err.name || err.price) {
        errors[index] = err;
      }
    });

    setFieldErrors(errors);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validateUseCases()) return;

    try {
      setSaving(true);
      setShowErrorMessage(false);
      await api.post(`/projects/${projectId}/use-cases`, { use_cases: useCases });
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
      // Reload data to get fresh state
      const data = await api.get<ProjectDetail>(`/projects/${projectId}`);
      setProject(data);
      const loaded = (data.useCases || []).map(uc => ({
        ...uc,
        price: Number(uc.price) || 0,
      }));
      setUseCases(loaded);
    } catch (error) {
      console.error('Failed to save use cases:', error);
      setShowErrorMessage(true);
      setErrorMessage(t('adminAddUseCases.saveError', 'Failed to save use cases'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/admin/projects/${projectId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-[var(--accent-primary)] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[var(--text-muted)]">{t('common.loading', 'Loading...')}</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-[var(--text-muted)]">{t('adminAddUseCases.projectNotFound', 'Project not found')}</p>
      </div>
    );
  }

  const existingUseCases = useCases.filter(uc => !uc.isNew && !uc.deleted);
  const newUseCases = useCases.filter(uc => uc.isNew && !uc.deleted);
  const deletedUseCases = useCases.filter(uc => uc.deleted);

  return (
    <div className="max-w-full overflow-x-hidden">
      <div className="mb-5 sm:mb-6">
        <h1 className="text-2xl lg:text-3xl font-bold text-[var(--text-primary)]">
          {t('adminAddUseCases.title', 'Admin Manage Use Cases')}
        </h1>
        <p className="mt-1.5 text-base sm:text-sm text-[var(--text-secondary)]">
          {t('adminAddUseCases.subtitle', 'Add new use cases with name, price in €, and mandatory status. Manage existing use cases directly in the table.')}
        </p>
      </div>

      {/* Success Message */}
      {showSuccessMessage && (
        <div className="mb-5 sm:mb-6 p-4 bg-green-600/15 border border-green-600/25 rounded-lg">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
            <p className="text-base sm:text-sm text-green-700 font-medium">
              {t('adminAddUseCases.successMessage', 'Use cases have been successfully saved')}
            </p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {showErrorMessage && (
        <div className="mb-5 sm:mb-6 p-4 bg-red-600/15 border border-red-600/25 rounded-lg">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-base sm:text-sm text-red-700 font-medium">{errorMessage}</p>
          </div>
        </div>
      )}

      {/* Use Cases Management Table */}
      <div className="bg-[var(--surface-base)] rounded-lg border border-[var(--border-subtle)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[600px]">
            <thead className="bg-[var(--surface-raised)]">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wide">
                  {t('adminAddUseCases.colName', 'Use Case Name')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wide">
                  {t('adminAddUseCases.colPrice', 'Price (€)')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wide">
                  {t('adminAddUseCases.colMandatory', 'Mandatory')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wide">
                  {t('adminAddUseCases.colActions', 'Actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border-subtle)]">
              {/* Existing Use Cases */}
              {existingUseCases.map((useCase) => {
                const actualIndex = useCases.indexOf(useCase);
                const errors = fieldErrors[actualIndex];
                return (
                  <tr key={useCase.projectUseCaseId || actualIndex} className="hover:bg-[var(--surface-raised)] transition-colors">
                    <td className="px-4 py-3">
                      <input
                        value={useCase.name}
                        onChange={(e) => updateUseCase(actualIndex, 'name', e.target.value)}
                        type="text"
                        placeholder={t('adminAddUseCases.namePlaceholder', 'Use case name')}
                        className={`w-full px-3 py-2 bg-[var(--surface-raised)] border rounded-lg text-base sm:text-sm focus:ring-1 focus:ring-[var(--accent-primary)] transition-colors ${
                          errors?.name ? 'border-red-500 focus:border-red-500' : 'border-[var(--border-subtle)] focus:border-[var(--accent-primary)]'
                        }`}
                      />
                      {errors?.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
                    </td>
                    <td className="px-4 py-3">
                      <input
                        // Render empty string when price is null/undefined so
                        // a freshly-added row doesn't show a misleading "0".
                        value={useCase.price == null ? '' : useCase.price}
                        onChange={(e) => {
                          const raw = e.target.value;
                          updateUseCase(
                            actualIndex,
                            'price',
                            raw === '' ? null : Number(raw),
                          );
                        }}
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        // appearance:textfield + hidden webkit spin buttons
                        // remove the browsers up/down arrows. Users type the
                        // value directly.
                        className={`w-full px-3 py-2 bg-[var(--surface-raised)] border rounded-lg text-base sm:text-sm focus:ring-1 focus:ring-[var(--accent-primary)] transition-colors [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${
                          errors?.price ? 'border-red-500 focus:border-red-500' : 'border-[var(--border-subtle)] focus:border-[var(--accent-primary)]'
                        }`}
                      />
                      {errors?.price && <p className="mt-1 text-xs text-red-600">{errors.price}</p>}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleMandatory(actualIndex)}
                        type="button"
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ${
                          useCase.mandatory ? 'bg-[var(--accent-primary)]' : 'bg-gray-300'
                        }`}
                      >
                        <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ${
                          useCase.mandatory ? 'translate-x-5' : 'translate-x-0'
                        }`}></span>
                      </button>
                      <div className="text-xs text-[var(--text-muted)] mt-1">
                        {useCase.mandatory ? t('adminAddUseCases.mandatory', 'Mandatory') : t('adminAddUseCases.optional', 'Optional')}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => removeUseCase(actualIndex)}
                        type="button"
                        className="px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-600/10 rounded transition-colors"
                      >
                        {t('adminAddUseCases.remove', 'Remove')}
                      </button>
                    </td>
                  </tr>
                );
              })}

              {/* New Use Case Rows (highlighted) */}
              {newUseCases.map((useCase) => {
                const actualIndex = useCases.indexOf(useCase);
                const errors = fieldErrors[actualIndex];
                return (
                  <tr key={`new-${actualIndex}`} className="hover:bg-[var(--surface-raised)] transition-colors bg-blue-50/30">
                    <td className="px-4 py-3">
                      <input
                        value={useCase.name}
                        onChange={(e) => updateUseCase(actualIndex, 'name', e.target.value)}
                        type="text"
                        placeholder={t('adminAddUseCases.newNamePlaceholder', 'Enter new use case name')}
                        className={`w-full px-3 py-2 bg-[var(--surface-raised)] border rounded-lg text-base sm:text-sm focus:ring-1 focus:ring-[var(--accent-primary)] transition-colors ${
                          errors?.name ? 'border-red-500 focus:border-red-500' : 'border-[var(--border-subtle)] focus:border-[var(--accent-primary)]'
                        }`}
                      />
                      {errors?.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
                    </td>
                    <td className="px-4 py-3">
                      <input
                        value={useCase.price == null ? '' : useCase.price}
                        onChange={(e) => {
                          const raw = e.target.value;
                          updateUseCase(
                            actualIndex,
                            'price',
                            raw === '' ? null : Number(raw),
                          );
                        }}
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder={t('adminAddUseCases.pricePlaceholder', 'Enter price in €')}
                        className={`w-full px-3 py-2 bg-[var(--surface-raised)] border rounded-lg text-base sm:text-sm focus:ring-1 focus:ring-[var(--accent-primary)] transition-colors [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${
                          errors?.price ? 'border-red-500 focus:border-red-500' : 'border-[var(--border-subtle)] focus:border-[var(--accent-primary)]'
                        }`}
                      />
                      {errors?.price && <p className="mt-1 text-xs text-red-600">{errors.price}</p>}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleMandatory(actualIndex)}
                        type="button"
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ${
                          useCase.mandatory ? 'bg-[var(--accent-primary)]' : 'bg-gray-300'
                        }`}
                      >
                        <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ${
                          useCase.mandatory ? 'translate-x-5' : 'translate-x-0'
                        }`}></span>
                      </button>
                      <div className="text-xs text-[var(--text-muted)] mt-1">
                        {useCase.mandatory ? t('adminAddUseCases.mandatory', 'Mandatory') : t('adminAddUseCases.optional', 'Optional')}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => removeUseCase(actualIndex)}
                        type="button"
                        className="px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-600/10 rounded transition-colors"
                      >
                        {t('adminAddUseCases.remove', 'Remove')}
                      </button>
                    </td>
                  </tr>
                );
              })}

              {/* Deleted rows (shown with strikethrough) */}
              {deletedUseCases.map((useCase) => (
                <tr key={`del-${useCase.projectUseCaseId}`} className="opacity-40">
                  <td className="px-4 py-3 line-through text-[var(--text-muted)]">{useCase.name}</td>
                  <td className="px-4 py-3 line-through text-[var(--text-muted)]">{fmtMoney(useCase.price, 2)}</td>
                  <td className="px-4 py-3 text-[var(--text-muted)]">{useCase.mandatory ? t('adminAddUseCases.mandatory', 'Mandatory') : t('adminAddUseCases.optional', 'Optional')}</td>
                  <td className="px-4 py-3 text-xs text-[var(--text-muted)]">{t('adminAddUseCases.markedForDeletion', 'Will be removed')}</td>
                </tr>
              ))}

              {/* Empty state */}
              {existingUseCases.length === 0 && newUseCases.length === 0 && deletedUseCases.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-[var(--text-muted)]">
                    {t('adminAddUseCases.noUseCases', 'No use cases yet. Click "Add New Use Case" to get started.')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add New Use Case Button */}
      <div className="mt-4 flex justify-start">
        <button
          onClick={addNewUseCase}
          type="button"
          className="px-4 py-2.5 bg-[var(--surface-raised)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--surface-overlay)] border border-[var(--border-subtle)] text-base sm:text-sm font-medium transition-colors flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
          </svg>
          {t('adminAddUseCases.addUseCase', 'Add New Use Case')}
        </button>
      </div>

      {/* Action Buttons */}
      <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
        <button
          onClick={handleCancel}
          type="button"
          className="w-full sm:w-auto px-5 py-3 sm:py-2.5 bg-[var(--surface-raised)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--surface-overlay)] border border-[var(--border-subtle)] text-base sm:text-sm font-medium transition-colors"
        >
          {t('common.cancel', 'Cancel')}
        </button>
        <button
          onClick={handleSubmit}
          disabled={saving}
          type="button"
          className="w-full sm:w-auto px-5 py-3 sm:py-2.5 bg-[var(--accent-primary)] text-[var(--accent-text)] rounded-lg hover:bg-[var(--accent-secondary)] text-base sm:text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? t('adminAddUseCases.saving', 'Saving...') : t('adminAddUseCases.saveChanges', 'Save Changes')}
        </button>
      </div>
    </div>
  );
}

export default AdminAddUseCasesPage;
